import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, Plus, Trash2, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BALL_OPTIONS = [
    { value: 'action_needed', label: 'Action Needed' },
    { value: 'waiting_on_lead', label: 'Waiting on Lead' },
    { value: 'na', label: 'N/A' },
];

const OPTION_LISTS = [
    ['prospect_qualification_options', 'Prospect Qualification'],
    ['prospect_status_options', 'Prospect Status'],
    ['prospect_source_options', 'Prospect Source'],
    ['funnel_options', 'Funnel'],
    ['page_variant_options', 'Page Variant'],
    ['form_variant_options', 'Form Variant'],
    ['product_variant_options', 'Product Variant'],
    ['reason_lost_options', 'Reason Lost'],
];

const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const inputCls = "px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10";

export default function LeadPipelineTab() {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [stages, setStages] = useState([]);
    const [optionLists, setOptionLists] = useState({});
    const [confirmForce, setConfirmForce] = useState(null); // 409 detail payload

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/lead-pipeline/settings`, { headers: getAuthHeader() });
            setStages((res.data.stages || []).map(s => ({ ...s })));
            const lists = {};
            OPTION_LISTS.forEach(([k]) => { lists[k] = (res.data[k] || []).join(', '); });
            setOptionLists(lists);
        } catch { toast.error('Failed to load settings'); }
        finally { setLoading(false); }
    }, [getAuthHeader]);

    useEffect(() => { load(); }, [load]);

    const updateStage = (i, patch) => setStages(prev => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
    const moveStage = (i, dir) => setStages(prev => {
        const next = [...prev]; const j = i + dir;
        if (j < 0 || j >= next.length) return prev;
        [next[i], next[j]] = [next[j], next[i]];
        return next.map((s, idx) => ({ ...s, order: idx }));
    });
    const removeStage = (i) => setStages(prev => prev.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx })));
    const addStage = () => setStages(prev => [...prev, { key: '', label: '', color: '#64748b', order: prev.length, amber_days: 3, red_days: 7, ball_in_court: 'action_needed', _new: true }]);

    const buildPayload = () => {
        const cleanStages = stages.map((s, idx) => ({
            key: s._new ? (slugify(s.label) || `stage_${idx}`) : s.key,
            label: s.label,
            color: s.color || '#64748b',
            order: idx,
            amber_days: (s.amber_days === '' || s.amber_days == null) ? null : Number(s.amber_days),
            red_days: (s.red_days === '' || s.red_days == null) ? null : Number(s.red_days),
            ball_in_court: s.ball_in_court || 'action_needed',
        }));
        const payload = { stages: cleanStages };
        OPTION_LISTS.forEach(([k]) => {
            payload[k] = (optionLists[k] || '').split(',').map(x => x.trim()).filter(Boolean);
        });
        return payload;
    };

    const doSave = async (force = false) => {
        if (stages.some(s => !(s.label || '').trim())) { toast.error('Every stage needs a label'); return; }
        setSaving(true);
        try {
            await axios.put(`${API}/api/lead-pipeline/settings${force ? '?force=true' : ''}`, buildPayload(), { headers: getAuthHeader() });
            toast.success('Lead pipeline settings saved');
            setConfirmForce(null);
            load();
        } catch (err) {
            if (err?.response?.status === 409) {
                setConfirmForce(err.response.data?.detail || { message: 'Some removed stages still have leads.' });
            } else {
                toast.error(err?.response?.data?.detail || 'Failed to save');
            }
        } finally { setSaving(false); }
    };

    if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-base font-semibold text-stone-800">Lead Pipeline</h3>
                    <p className="text-sm text-stone-500">Stages and dropdown options for the lead-level sales pipeline. Used on the lead record (and the kanban board, coming soon).</p>
                </div>
                <Button onClick={() => doSave(false)} disabled={saving}>
                    {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
                </Button>
            </div>

            {/* Stages */}
            <div className="bg-white rounded-xl border border-stone-200">
                <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-stone-700">Stages</h4>
                    <Button variant="outline" size="sm" onClick={addStage}><Plus className="w-4 h-4 mr-1" /> Add stage</Button>
                </div>
                <div className="p-4 space-y-2">
                    <div className="hidden sm:grid grid-cols-[1.4fr_2.4rem_1fr_5rem_5rem_2.5rem] gap-2 text-xs text-stone-400 px-1">
                        <span>Label</span><span>Color</span><span>Ball in court</span><span>Amber d</span><span>Red d</span><span></span>
                    </div>
                    {stages.map((s, i) => (
                        <div key={s.key || `new-${i}`} className="grid grid-cols-2 sm:grid-cols-[1.4fr_2.4rem_1fr_5rem_5rem_2.5rem] gap-2 items-center">
                            <input className={inputCls} value={s.label} placeholder="Stage label" onChange={e => updateStage(i, { label: e.target.value })} />
                            <input type="color" className="h-8 w-full rounded border border-stone-200 p-0.5" value={s.color || '#64748b'} onChange={e => updateStage(i, { color: e.target.value })} />
                            <select className={inputCls} value={s.ball_in_court || 'action_needed'} onChange={e => updateStage(i, { ball_in_court: e.target.value })}>
                                {BALL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            <input type="number" className={inputCls} value={s.amber_days ?? ''} placeholder="—" onChange={e => updateStage(i, { amber_days: e.target.value })} />
                            <input type="number" className={inputCls} value={s.red_days ?? ''} placeholder="—" onChange={e => updateStage(i, { red_days: e.target.value })} />
                            <div className="flex items-center gap-0.5">
                                <button onClick={() => moveStage(i, -1)} className="p-1 text-stone-400 hover:text-stone-700" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                                <button onClick={() => moveStage(i, 1)} className="p-1 text-stone-400 hover:text-stone-700" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                                <button onClick={() => removeStage(i)} className="p-1 text-stone-400 hover:text-red-600" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                        </div>
                    ))}
                    {stages.length === 0 && <p className="text-sm text-stone-400 py-4 text-center">No stages — add one.</p>}
                </div>
            </div>

            {/* Option lists */}
            <div className="bg-white rounded-xl border border-stone-200">
                <div className="px-4 py-3 border-b border-stone-100"><h4 className="text-sm font-semibold text-stone-700">Dropdown options</h4></div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {OPTION_LISTS.map(([k, label]) => (
                        <div key={k}>
                            <label className="block text-xs font-medium text-stone-600 mb-1">{label} <span className="text-stone-400">(comma-separated)</span></label>
                            <input className={`w-full ${inputCls}`} value={optionLists[k] || ''} onChange={e => setOptionLists(p => ({ ...p, [k]: e.target.value }))} />
                        </div>
                    ))}
                </div>
            </div>

            {/* Force-confirm when removing a stage that still has leads */}
            {confirmForce && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setConfirmForce(null)}>
                    <div className="bg-white rounded-xl border border-stone-200 max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-2 text-amber-600"><AlertTriangle className="w-5 h-5" /><h4 className="font-semibold">Stages still in use</h4></div>
                        <p className="text-sm text-stone-600 mb-3">{confirmForce.message || 'Some removed stages still have leads.'}</p>
                        {Array.isArray(confirmForce.stages_in_use) && (
                            <ul className="text-sm text-stone-700 mb-4 list-disc pl-5">
                                {confirmForce.stages_in_use.map(s => <li key={s.key}><b>{s.key}</b> — {s.lead_count} lead{s.lead_count === 1 ? '' : 's'}</li>)}
                            </ul>
                        )}
                        <p className="text-xs text-stone-500 mb-4">Saving with force keeps those leads on a now-undefined stage (label/color won't resolve until reassigned).</p>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setConfirmForce(null)}>Cancel</Button>
                            <Button onClick={() => doSave(true)} disabled={saving}>Save anyway</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
